import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { communicationApi, audienceMemberApi } from '../../api';

const COMMUNICATION_TYPES = [
  { value: 'NOTE', label: 'Internal Note', icon: DocumentTextIcon, color: 'bg-gray-100 text-gray-800' },
  { value: 'CALL', label: 'Phone Call', icon: PhoneIcon, color: 'bg-blue-100 text-blue-800' },
  { value: 'EMAIL', label: 'Email', icon: EnvelopeIcon, color: 'bg-green-100 text-green-800' },
  { value: 'WHATSAPP', label: 'WhatsApp', icon: ChatBubbleLeftRightIcon, color: 'bg-emerald-100 text-emerald-800' },
  { value: 'SMS', label: 'SMS', icon: ChatBubbleLeftRightIcon, color: 'bg-purple-100 text-purple-800' },
  { value: 'MEETING', label: 'Meeting', icon: UserGroupIcon, color: 'bg-orange-100 text-orange-800' },
];

const DIRECTIONS = [
  { value: 'OUTBOUND', label: 'Outbound', icon: ArrowUpIcon },
  { value: 'INBOUND', label: 'Inbound', icon: ArrowDownIcon },
];

const getStatusStyle = (status) => {
  switch (status) {
    case 'PAYING_USER':
      return { label: 'Paying User', color: 'bg-green-100 text-green-800' };
    case 'REQUIRES_ACTION':
      return { label: 'Requires Action', color: 'bg-red-100 text-red-800' };
    case 'WAITING_MEMBER_COMM':
      return { label: 'Waiting Response', color: 'bg-yellow-100 text-yellow-800' };
    case 'CONTACTED':
      return { label: 'Contacted', color: 'bg-blue-100 text-blue-800' };
    default:
      return { label: 'Not Contacted', color: 'bg-gray-100 text-gray-600' };
  }
};

const MemberCommunicationModal = ({ show, member, onClose, onMemberUpdated }) => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  // New communication form
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    communicationType: 'NOTE',
    direction: 'OUTBOUND',
    subject: '',
    content: '',
    followUpDate: '',
    communicationDate: new Date().toISOString().slice(0, 16), // datetime-local format
    currentSoft: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [softwareOptions, setSoftwareOptions] = useState([]);

  const fetchCommunications = useCallback(async () => {
    if (!member?.id) return;
    setLoading(true);
    try {
      const data = await communicationApi.getByMemberId(member.id, page, pageSize);
      setCommunications(data?.communications || []);
      setTotal(data?.total || 0);
    } catch (error) {
      console.error('Error fetching communications:', error);
      toast.error('Failed to fetch communications');
    } finally {
      setLoading(false);
    }
  }, [member?.id, page]);

  useEffect(() => {
    if (show && member?.id) {
      fetchCommunications();
    }
  }, [show, member?.id, fetchCommunications]);

  useEffect(() => {
    if (!show) return;

    const loadSoftwareOptions = async () => {
      try {
        const values = await audienceMemberApi.getDistinctSoftware();
        setSoftwareOptions(Array.isArray(values) ? values : []);
      } catch (error) {
        console.error('Error fetching distinct software options:', error);
        setSoftwareOptions([]);
      }
    };

    loadSoftwareOptions();
  }, [show]);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setShowAddForm(Boolean(member?.openAddForm));
      setFormData({
        communicationType: 'NOTE',
        direction: 'OUTBOUND',
        subject: '',
        content: '',
        followUpDate: '',
        communicationDate: new Date().toISOString().slice(0, 16),
        currentSoft: member?.currentSoft || '',
      });
      setPage(1);
    }
  }, [show, member?.openAddForm, member?.currentSoft]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.content.trim()) {
      toast.error('Content is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        memberId: member.id,
        communicationType: formData.communicationType,
        direction: formData.direction,
        subject: formData.subject || null,
        content: formData.content,
        followUpDate: formData.followUpDate ? new Date(formData.followUpDate).toISOString() : null,
        communicationDate: new Date(formData.communicationDate).toISOString(),
        currentSoft: formData.currentSoft?.trim() || null,
      };

      await communicationApi.add(payload);
      toast.success('Communication added successfully');
      setShowAddForm(false);
      setFormData({
        communicationType: 'NOTE',
        direction: 'OUTBOUND',
        subject: '',
        content: '',
        followUpDate: '',
        communicationDate: new Date().toISOString().slice(0, 16),
        currentSoft: member?.currentSoft || '',
      });
      fetchCommunications();
      // Notify parent that member may have been updated (status change)
      if (onMemberUpdated) {
        onMemberUpdated();
      }
    } catch (error) {
      console.error('Error adding communication:', error);
      toast.error('Failed to add communication');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (communicationId) => {
    if (!window.confirm('Are you sure you want to delete this communication?')) return;
    try {
      await communicationApi.delete(communicationId);
      toast.success('Communication deleted');
      fetchCommunications();
    } catch (error) {
      console.error('Error deleting communication:', error);
      toast.error('Failed to delete communication');
    }
  };

  const handleMarkFollowUpCompleted = async (communicationId, isCompleted) => {
    try {
      await communicationApi.markFollowUpCompleted(communicationId);
      toast.success(`Follow-up marked as ${isCompleted ? 'pending' : 'completed'}`);
      fetchCommunications();
    } catch (error) {
      console.error('Error updating follow-up status:', error);
      toast.error('Failed to update follow-up status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeInfo = (type) => {
    return COMMUNICATION_TYPES.find((t) => t.value === type) || COMMUNICATION_TYPES[0];
  };

  if (!show) return null;

  const memberInfo = member?.memberInfo || {};
  const statusStyle = getStatusStyle(member?.status);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-purple-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-white">
                  CRM - {memberInfo.name || 'Unknown'}
                </h3>
                <div className="flex items-center gap-4 mt-1">
                  {memberInfo.email && (
                    <span className="text-purple-200 text-sm flex items-center">
                      <EnvelopeIcon className="h-4 w-4 mr-1" />
                      {memberInfo.email}
                    </span>
                  )}
                  {memberInfo.phone && (
                    <span className="text-purple-200 text-sm flex items-center">
                      <PhoneIcon className="h-4 w-4 mr-1" />
                      {memberInfo.phone}
                    </span>
                  )}
                  <span className="text-purple-200 text-sm">
                    Current Software: {member?.currentSoft || 'N/A'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle.color}`}>
                  {statusStyle.label}
                </span>
                <button
                  onClick={onClose}
                  className="text-white hover:text-purple-200 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {/* Add Communication Button */}
            {!showAddForm && (
              <button
                onClick={() => setShowAddForm(true)}
                className="mb-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Communication
              </button>
            )}

            {/* Add Communication Form */}
            {showAddForm && (
              <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-4">New Communication</h4>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Communication Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        value={formData.communicationType}
                        onChange={(e) => setFormData({ ...formData, communicationType: e.target.value })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                      >
                        {COMMUNICATION_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Direction (hidden for NOTE type) */}
                    {formData.communicationType !== 'NOTE' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Direction
                        </label>
                        <select
                          value={formData.direction}
                          onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        >
                          {DIRECTIONS.map((dir) => (
                            <option key={dir.value} value={dir.value}>
                              {dir.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Communication Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.communicationDate}
                        onChange={(e) => setFormData({ ...formData, communicationDate: e.target.value })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                      />
                    </div>

                    {/* Follow-up Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Follow-up Date (Optional)
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.followUpDate}
                        onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Brief subject or title..."
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                    />
                  </div>

                  {/* Current Software */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Software
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={formData.currentSoft || ''}
                        onChange={(e) => setFormData({ ...formData, currentSoft: e.target.value })}
                        placeholder="Type software name (e.g. ServiceM)"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        list="member-communication-software-options"
                      />
                      {softwareOptions.length > 0 && (
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              setFormData((prev) => ({ ...prev, currentSoft: e.target.value }));
                            }
                          }}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        >
                          <option value="">Pick from existing software</option>
                          {softwareOptions.map((software) => (
                            <option key={software} value={software}>{software}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <datalist id="member-communication-software-options">
                      {softwareOptions.map((software) => (
                        <option key={software} value={software} />
                      ))}
                    </datalist>
                    <p className="mt-1 text-xs text-gray-500">
                      Saving this communication will also update the member's current software.
                    </p>
                  </div>

                  {/* Content */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Content <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={4}
                      placeholder="Describe the communication..."
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                      required
                    />
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                    >
                      {submitting ? 'Saving...' : 'Save Communication'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Communications List */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Communication History ({total} entries)
              </h4>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading...</p>
                </div>
              ) : communications.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto" />
                  <p className="mt-2 text-sm text-gray-500">No communications yet</p>
                  <p className="text-xs text-gray-400">Add your first communication to start tracking</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {communications.map((comm) => {
                    const typeInfo = getTypeInfo(comm.communicationType);
                    const TypeIcon = typeInfo.icon;
                    const isInbound = comm.direction === 'INBOUND';

                    return (
                      <div
                        key={comm.id}
                        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${typeInfo.color}`}>
                              <TypeIcon className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeInfo.color}`}>
                                  {typeInfo.label}
                                </span>
                                {comm.communicationType !== 'NOTE' && (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    isInbound ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {isInbound ? '← Inbound' : '→ Outbound'}
                                  </span>
                                )}
                                {comm.followUpDate && (
                                  <div className="inline-flex items-center gap-2">
                                    {comm.followUpCompleted ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                                        Follow-up Completed
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                        <ClockIcon className="h-3 w-3 mr-1" />
                                        Follow-up: {formatDate(comm.followUpDate)}
                                      </span>
                                    )}
                                    {comm.followUpDate && !comm.followUpCompleted && (
                                      <button
                                        onClick={() => handleMarkFollowUpCompleted(comm.id, comm.followUpCompleted)}
                                        className="text-xs px-2 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100 transition-colors border border-green-200"
                                        title="Mark follow-up as completed"
                                      >
                                        Mark Done
                                      </button>
                                    )}
                                    {comm.followUpDate && comm.followUpCompleted && (
                                      <button
                                        onClick={() => handleMarkFollowUpCompleted(comm.id, comm.followUpCompleted)}
                                        className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors border border-gray-300"
                                        title="Revert to pending"
                                      >
                                        Revert
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                              {comm.subject && (
                                <p className="mt-1 text-sm font-medium text-gray-900">{comm.subject}</p>
                              )}
                                            {comm.currentSoft && (
                                              <p className="mt-1 text-xs text-gray-500">Software: {comm.currentSoft}</p>
                                            )}
                              <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{comm.content}</p>
                              <p className="mt-2 text-xs text-gray-400">
                                {formatDate(comm.communicationDate)}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDelete(comm.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {total > pageSize && (
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-gray-700">
                    Page {page} of {Math.ceil(total / pageSize)}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page * pageSize >= total}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberCommunicationModal;

